import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ReactReduxContext, useDispatch, useSelector } from 'react-redux';

import { useMount } from '@provider-portal/common/hooks/useMount';

import { NetworkRequestState } from '@bolteu/redux-session';
import { waitForAccessTokenUpdate } from '@bolteu/redux-session/build/src/accessTokenHooks';
import { AuthType, SupportWidget, UnreadCase } from '@bolteu/support-widget';
import { InitProps } from '@bolteu/support-widget/dist/widget';

import { useSelectedProviderId } from 'hooks/useSelectedProvider';
import { selectAccountStage } from 'rdx/account';
import { selectSessionAccessTokenState } from 'rdx/session/selectors';
import {
  getMainEntryWidgetUrlAsyncAction,
  getSupportCaseWidgetUrlAsyncAction,
  getSupportWidgetMqttConnectionInfoAsyncAction,
  getUnreadCasesWithLastMessageAsyncAction,
  resetSelectedSupportCaseAction,
  resetSupportWidgetDataAction,
  selectFetchingMainEntryWidgetUrlState,
  selectFetchingMqttConnectionInfo,
  selectFetchingSupportCaseUrlState,
  selectFetchingUnreadCasesState,
  selectMqttConnectionInfoState,
  selectSupportCaseUrlState,
  selectSupportMainEntryWidgetUrlState,
  selectSupportSelectedCaseState,
  selectUnreadCasesState,
} from 'rdx/support';
import { consoleLog } from 'utils/consoleLog';

import { useCommonAppServices } from 'root/CommonAppServicesContext';

enum SupportWidgetMixpanelEventType {
  SUPPORT_WIDGET_CLICK = 'support_widget_click',
  SUPPORT_WIDGET_EMAIL_REDIRECT = 'support_widget_email_redirect',
}

export const useCustomerSupport = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { mixpanelService } = useCommonAppServices();
  const [isSupportWidgetInitialised, setIsSupportWidgetInitialised] = useState(false);
  const [isDataReset, setIsDataReset] = useState(false);

  const accessToken = useRef(useSelector(selectSessionAccessTokenState));
  const updatePromise = useRef<Promise<string> | null>(null);
  const mainEntryWidgetUrl = useSelector(selectSupportMainEntryWidgetUrlState);

  const supportCaseUrl = useSelector(selectSupportCaseUrlState);
  const unreadCases = useSelector(selectUnreadCasesState);
  const mqttConnectionInfo = useSelector(selectMqttConnectionInfoState);

  const selectedProviderId = useSelectedProviderId();
  const selectedCase = useSelector(selectSupportSelectedCaseState);
  const accountStage = useSelector(selectAccountStage);

  const fetchingMainEntryWidgetUrlState = useSelector(selectFetchingMainEntryWidgetUrlState);
  const fetchingSupportCaseUrlState = useSelector(selectFetchingSupportCaseUrlState);
  const fetchingUnreadCasesState = useSelector(selectFetchingUnreadCasesState);
  const fetchingMqttConnectionInfo = useSelector(selectFetchingMqttConnectionInfo);

  const [fetchUnreadCasesPromiseHandlers, setFetchUnreadCasesPromiseHandlers] = useState(null);
  const fetchUnreadCasesPromise = useCallback(() => {
    return new Promise<UnreadCase[]>((resolve, reject) => {
      dispatch(getUnreadCasesWithLastMessageAsyncAction.request());
      setFetchUnreadCasesPromiseHandlers({ resolve, reject });
    });
  }, [dispatch]);

  const { store } = useContext(ReactReduxContext);
  const isTokenExpired = (token: string) => {
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const expiryTime = tokenPayload.exp * 1000;
    return expiryTime < Date.now();
  };

  const reqTokenCallback = useCallback(async (): Promise<string> => {
    if (isTokenExpired(accessToken.current)) {
      if (updatePromise.current) {
        return updatePromise.current;
      }
      updatePromise.current = new Promise((resolve) => {
        waitForAccessTokenUpdate(store).then((newToken) => {
          resolve(newToken);
          accessToken.current = newToken;
          updatePromise.current = null;
        });
      });

      return updatePromise.current;
    }

    return accessToken.current;
  }, [accessToken, store]);

  const supportWidget = useMemo(() => new SupportWidget(), []);

  const getSelectedCaseId = useCallback(() => {
    if (selectedCase && selectedCase.provider_id === selectedProviderId) {
      return selectedCase.case_id;
    }
    return undefined;
  }, [selectedCase, selectedProviderId]);

  const toggleCallback = useCallback(() => {
    if (selectedProviderId) {
      mixpanelService.trackProviderEvent(SupportWidgetMixpanelEventType.SUPPORT_WIDGET_CLICK, {
        providerId: selectedProviderId,
        lifecycleStatus: accountStage,
      });
    }
  }, [accountStage, mixpanelService, selectedProviderId]);

  const clearSelectedCase = useCallback(() => {
    if (selectedCase) {
      if (selectedProviderId && getSelectedCaseId()) {
        mixpanelService.trackProviderEvent(
          SupportWidgetMixpanelEventType.SUPPORT_WIDGET_EMAIL_REDIRECT,
          {
            providerId: selectedProviderId,
            lifecycleStatus: accountStage,
          },
        );
      }

      dispatch(resetSelectedSupportCaseAction());
    }
  }, [
    accountStage,
    dispatch,
    getSelectedCaseId,
    mixpanelService,
    selectedCase,
    selectedProviderId,
  ]);

  const getInitProps = useCallback((): InitProps => {
    return {
      url: mainEntryWidgetUrl,
      auth: { type: AuthType.JWT, requestToken: reqTokenCallback },
      locale: intl.locale,
      supportCaseUrl,
      unreadCases,
      getUnreadCases: fetchUnreadCasesPromise,
      mqttConnectionInfo,
      styleProps: { alwaysShowFloatingButton: true },
      selectedCaseId: getSelectedCaseId(),
      toggleCallback,
    };
  }, [
    fetchUnreadCasesPromise,
    getSelectedCaseId,
    intl.locale,
    mainEntryWidgetUrl,
    mqttConnectionInfo,
    reqTokenCallback,
    supportCaseUrl,
    toggleCallback,
    unreadCases,
  ]);

  const isDataInitialized = useCallback((): boolean => {
    return (
      fetchingMainEntryWidgetUrlState === NetworkRequestState.success &&
      fetchingSupportCaseUrlState === NetworkRequestState.success &&
      fetchingUnreadCasesState === NetworkRequestState.success &&
      fetchingMqttConnectionInfo === NetworkRequestState.success &&
      mainEntryWidgetUrl !== '' &&
      supportCaseUrl !== '' &&
      mqttConnectionInfo.broker_url !== '' &&
      mqttConnectionInfo.user_id !== 0 &&
      accessToken.current !== ''
    );
  }, [
    accessToken,
    fetchingMainEntryWidgetUrlState,
    fetchingMqttConnectionInfo,
    fetchingSupportCaseUrlState,
    fetchingUnreadCasesState,
    mainEntryWidgetUrl,
    mqttConnectionInfo.broker_url,
    mqttConnectionInfo.user_id,
    supportCaseUrl,
  ]);

  useEffect(() => {
    if (fetchUnreadCasesPromiseHandlers) {
      if (fetchingUnreadCasesState === NetworkRequestState.success) {
        fetchUnreadCasesPromiseHandlers.resolve(unreadCases);
        setFetchUnreadCasesPromiseHandlers(null);
      } else if (fetchingUnreadCasesState === NetworkRequestState.failure) {
        fetchUnreadCasesPromiseHandlers.reject(new Error('Error fetching unread cases data'));
        setFetchUnreadCasesPromiseHandlers(null);
      }
    }
  }, [
    fetchUnreadCasesPromise,
    unreadCases,
    fetchingUnreadCasesState,
    fetchUnreadCasesPromiseHandlers,
  ]);

  // Trigger data initialization
  useMount(() => {
    dispatch(resetSupportWidgetDataAction());
    setIsDataReset(true);

    dispatch(getMainEntryWidgetUrlAsyncAction.request());
    dispatch(getSupportCaseWidgetUrlAsyncAction.request());
    dispatch(getUnreadCasesWithLastMessageAsyncAction.request());
    dispatch(getSupportWidgetMqttConnectionInfoAsyncAction.request());
  });

  // SupportWidget initialisation
  useEffect(() => {
    if (!isSupportWidgetInitialised && isDataInitialized() && isDataReset) {
      try {
        supportWidget.init(getInitProps());
        setIsSupportWidgetInitialised(true);
        clearSelectedCase();
      } catch (_error) {
        consoleLog('Failed initialising SupportWidget', _error);
      }
    }
  }, [
    clearSelectedCase,
    dispatch,
    getInitProps,
    isDataInitialized,
    isDataReset,
    isSupportWidgetInitialised,
    mainEntryWidgetUrl,
    supportWidget,
  ]);

  // SupportWidget un-initialisation
  useEffect(() => {
    return () => {
      if (isSupportWidgetInitialised) {
        supportWidget.destroy().catch((e) => consoleLog('Failed destroying support widget', e));
      }
    };
  }, [isSupportWidgetInitialised, supportWidget]);
};
